<template lang="pug">
.search-bar-block-editor
  .editor-header
    h3 Search Bar

  .editor-form
    el-form(:model="formData" label-position="top" :rules="rules" ref="formRef")
      el-form-item(label="Search Type" prop="searchType")
        el-select(
          v-model="formData.searchType"
          placeholder="Select search type"
          size="default"
        )
          el-option(
            label="Non-profits"
            value="non-profits"
          )
          el-option(
            label="Organizations"
            value="organizations"
          )
          el-option(
            label="Users"
            value="users"
          )
          el-option(
            label="Stickers"
            value="stickers"
          )

      el-form-item(label="Placeholder Text" prop="placeholderText")
        el-input(
          v-model="formData.placeholderText"
          placeholder="Enter placeholder text"
          size="default"
        )

      .editor-actions
        el-button(
          @click="handleCancel"
          size="default"
        ) Cancel
        el-button(
          type="primary"
          @click="handleSave"
          size="default"
          :loading="saving"
        ) Save
</template>

<script>
import {
  defineComponent, ref, reactive, onMounted,
} from 'vue';
import { ElMessage } from 'element-plus';
import useStaticPageUtils from '../shared/index.ts';

export default defineComponent({
  name: 'SearchBarBlock',
  props: {
    block: {
      type: Object,
      default: null,
    },
    isCreating: {
      type: Boolean,
      default: false,
    },
    pageId: {
      type: String,
      required: true,
    },
    staticPageData: {
      type: Object,
      default: null,
    },
  },
  emits: ['save', 'cancel'],
  setup(props, { emit }) {
    const { createOrUpdateSearchBarBlock } = useStaticPageUtils();
    const formRef = ref(null);
    const saving = ref(false);

    // Form data
    const formData = reactive({
      searchType: props.block?.details?.searchType || 'non-profits',
      placeholderText: props.block?.details?.placeholderText || '',
    });

    // Form validation rules
    const rules = {
      searchType: [
        { required: true, message: 'Please select a search type', trigger: 'change' },
      ],
      placeholderText: [
        { required: true, message: 'Please enter placeholder text', trigger: 'blur' },
      ],
    };

    // Handle save
    async function handleSave() {
      if (!formRef.value) return;

      try {
        await formRef.value.validate();
        saving.value = true;

        const input = {
          searchType: formData.searchType,
          placeholderText: formData.placeholderText,
          sortOrder: props.isCreating ? null : (props.block?.sortOrder || 0),
          parentId: props.pageId,
          id: props.isCreating ? null : props.block?.id,
        };

        const result = await createOrUpdateSearchBarBlock(input, props.staticPageData);

        ElMessage.success(props.isCreating ? 'Search block created successfully' : 'Search block updated successfully');
        emit('save', result);
      } catch (error) {
        console.error('Error saving search bar block:', error);
        ElMessage.error('Failed to save search bar block');
      } finally {
        saving.value = false;
      }
    }

    // Handle cancel
    function handleCancel() {
      emit('cancel');
    }

    return {
      formRef,
      formData,
      rules,
      saving,
      handleSave,
      handleCancel,
    };
  },
});
</script>

<style scoped>
.search-bar-block-editor {
  width: 100%;
}

.editor-form {
  max-width: 500px;
  margin-top: 20px;
}

.editor-actions {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}
</style>
